import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

// Confetti piece shapes
const SHAPES = ['square', 'circle', 'triangle', 'ribbon'];

// Vibrant celebration colors
const COLORS = [
  '#3b82f6', // blue-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
  '#f59e0b', // amber-500
  '#22c55e', // green-500
  '#06b6d4', // cyan-500
  '#f43f5e', // rose-500
  '#a855f7', // purple-500
  '#fbbf24', // yellow-400
  '#10b981', // emerald-500
];

/**
 * Generate a single confetti piece with random properties
 */
const createConfettiPiece = (index, totalPieces) => {
  const shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
  const color = COLORS[Math.floor(Math.random() * COLORS.length)];

  // Spread pieces across the screen width with some randomness
  const spreadFactor = 100 / totalPieces;
  const baseX = (index * spreadFactor) + (Math.random() * spreadFactor * 0.5);

  return {
    id: `confetti-${index}-${Date.now()}`,
    shape,
    color,
    x: baseX, // percentage
    startY: -10 - (Math.random() * 20), // start above viewport
    endY: 110 + (Math.random() * 20), // end below viewport
    size: 8 + Math.random() * 12, // 8-20px
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 1080, // -540 to 540 degrees
    swayAmplitude: 20 + Math.random() * 40, // horizontal sway
    swayFrequency: 2 + Math.random() * 3, // sway cycles
    duration: 3000 + Math.random() * 2000, // 3-5 seconds
    delay: Math.random() * 1000, // stagger the start
  };
};

/**
 * Individual confetti piece component
 */
const ConfettiPiece = ({ piece }) => {
  const [style, setStyle] = useState({
    opacity: 0,
    transform: `translateY(${piece.startY}vh) rotate(${piece.rotation}deg)`,
  });

  useEffect(() => {
    const startTime = Date.now();
    let animationFrame;

    const animate = () => {
      const elapsed = Date.now() - startTime - piece.delay;

      if (elapsed < 0) {
        animationFrame = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min(elapsed / piece.duration, 1);

      // Easing function for natural fall
      const easeOut = 1 - Math.pow(1 - progress, 2);

      // Calculate position
      const y = piece.startY + (piece.endY - piece.startY) * easeOut;

      // Sway effect
      const swayOffset = Math.sin(progress * Math.PI * piece.swayFrequency) * piece.swayAmplitude;

      // Rotation
      const currentRotation = piece.rotation + (piece.rotationSpeed * progress);

      // Fade out near the end
      const opacity = progress > 0.8 ? 1 - ((progress - 0.8) / 0.2) : 1;

      setStyle({
        opacity,
        transform: `translateX(${swayOffset}px) translateY(${y}vh) rotate(${currentRotation}deg)`,
      });

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [piece]);

  // Render different shapes
  const renderShape = () => {
    const baseStyle = {
      width: piece.size,
      height: piece.size,
      backgroundColor: piece.color,
    };

    switch (piece.shape) {
      case 'circle':
        return <div style={{ ...baseStyle, borderRadius: '50%' }} />;

      case 'triangle':
        return (
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: `${piece.size / 2}px solid transparent`,
              borderRight: `${piece.size / 2}px solid transparent`,
              borderBottom: `${piece.size}px solid ${piece.color}`,
              backgroundColor: 'transparent',
            }}
          />
        );

      case 'ribbon':
        return (
          <div
            style={{
              width: piece.size * 0.4,
              height: piece.size * 1.5,
              backgroundColor: piece.color,
              borderRadius: '2px',
            }}
          />
        );

      case 'square':
      default:
        return <div style={{ ...baseStyle, borderRadius: '2px' }} />;
    }
  };

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${piece.x}%`,
        top: 0,
        ...style,
      }}
    >
      {renderShape()}
    </div>
  );
};

/**
 * ConfettiCelebration - Fullscreen confetti explosion effect
 *
 * Props:
 * - active: boolean - Whether to show the confetti
 * - duration: number - How long the celebration lasts (ms)
 * - pieceCount: number - Number of confetti pieces
 * - onComplete: function - Callback when animation finishes
 */
const ConfettiCelebration = ({
  active = false,
  duration = 4000,
  pieceCount = 100,
  onComplete,
}) => {
  const [pieces, setPieces] = useState([]);
  const [visible, setVisible] = useState(false);

  // Generate confetti pieces when activated
  useEffect(() => {
    if (active) {
      const newPieces = Array.from({ length: pieceCount }, (_, i) =>
        createConfettiPiece(i, pieceCount)
      );
      setPieces(newPieces);
      setVisible(true);

      // Clean up after duration
      const timer = setTimeout(() => {
        setVisible(false);
        setPieces([]);
        onComplete?.();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [active, pieceCount, duration, onComplete]);

  if (!visible || pieces.length === 0) return null;

  const content = (
    <div
      className="fixed inset-0 z-[300] pointer-events-none overflow-hidden"
      aria-hidden="true"
    >
      {pieces.map(piece => (
        <ConfettiPiece key={piece.id} piece={piece} />
      ))}
    </div>
  );

  return createPortal(content, document.body);
};

/**
 * useConfetti - Hook for triggering confetti programmatically
 */
export const useConfetti = () => {
  const [isActive, setIsActive] = useState(false);
  const [config, setConfig] = useState({});

  const trigger = useCallback((options = {}) => {
    setConfig(options);
    setIsActive(true);
  }, []);

  const handleComplete = useCallback(() => {
    setIsActive(false);
    config.onComplete?.();
  }, [config]);

  const ConfettiComponent = useMemo(() => {
    return (
      <ConfettiCelebration
        active={isActive}
        duration={config.duration}
        pieceCount={config.pieceCount}
        onComplete={handleComplete}
      />
    );
  }, [isActive, config.duration, config.pieceCount, handleComplete]);

  return {
    trigger,
    isActive,
    ConfettiComponent,
  };
};

export default ConfettiCelebration;
